/* eslint-disable no-unused-vars */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import livekitService from '../services/livekitService';
import { Track } from 'livekit-client'; // --- ADD THIS IMPORT ---

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState({
    isAuthenticated: false,
    sessionId: null,
    roomName: null,
    livekitToken: null,
    livekitUrl: null,
    userIdentity: null,
    appId: null,
    apiKey: null,
    expiresIn: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [participants, setParticipants] = useState([]);

  // --- THIS IS THE FIRST NEW FUNCTION ---
  const handleTrackSubscribed = (track, publication, participant) => {
    // We only care about the AI's audio track (which is remote)
    if (track.kind === Track.Kind.Audio && !participant.isLocal) {
      console.log(`🔊 AI voice connected from ${participant.identity}. Attaching audio element...`);
      // This creates and manages the <audio> element for you.
      // It will be attached to the document body where the browser can play it.
      track.attach();
      console.log('✅ Audio track attached. You should now hear the AI.');
    }
  };

  // --- THIS IS THE SECOND NEW FUNCTION ---
  const handleTrackUnsubscribed = (track) => {
    // This correctly removes the audio element when the track is gone.
    track.detach().forEach((element) => element.remove());
  };

  // Login function
  const login = async (userIdentity, appId, apiKey, language = 'en-US') => {
    setLoading(true);
    setError(null);

    try {
      if (!appId || appId.trim() === '') throw new Error('App ID is required for authentication');
      if (!userIdentity || userIdentity.trim() === '') throw new Error('User identity is required');
      if (!apiKey || apiKey.trim() === '') throw new Error('API key is required');
      if (appId.trim().length < 10) throw new Error('Invalid App ID format');

      const response = await apiService.login(userIdentity, appId, apiKey, language);
      
      setAuthData({
        isAuthenticated: true,
        sessionId: response.session_id,
        roomName: response.room_name,
        livekitToken: response.livekit_token,
        livekitUrl: response.livekit_url,
        userIdentity: response.user_identity,
        appId: response.app_id,
        apiKey: apiKey,
        expiresIn: response.expires_in
      });

      localStorage.setItem('authData', JSON.stringify({
        sessionId: response.session_id,
        userIdentity: response.user_identity,
        appId: response.app_id,
        apiKey: apiKey
      }));

      console.log('✅ Authentication successful');
      
      if (response.livekit_url && response.livekit_token) {
        console.log('🔗 AuthContext: Auto-connecting to voice after login...');
        try {
          await connectToVoiceWithData(response.livekit_url, response.livekit_token, response.room_name);
        } catch (voiceError) {
          console.warn('⚠️ AuthContext: Auto voice connection failed:', voiceError);
        }
      }
      
      return response;

    } catch (error) {
      setError(error.message);
      console.error('❌ Authentication failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // LiveKit Voice Connection Functions
  const connectToVoiceWithData = async (livekitUrl, livekitToken, roomName) => {
    try {
      console.log('🔗 AuthContext: Connecting to voice with data...', { livekitUrl, roomName, hasToken: !!livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE TWO LINES ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(livekitUrl, livekitToken, roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      
      setTimeout(async () => {
        try {
          console.log('🎤 AuthContext: Auto-enabling microphone...');
          await enableMicrophone();
          console.log('✅ AuthContext: Microphone auto-enabled successfully');
        } catch (micError) {
          console.error('⚠️ AuthContext: Microphone auto-enable failed:', micError);
          console.log('💡 Please allow microphone access to talk to the AI');
        }
      }, 2000);
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  const connectToVoice = async () => {
    try {
      console.log('🔗 AuthContext: Connecting to voice...', { livekitUrl: authData.livekitUrl, roomName: authData.roomName, hasToken: !!authData.livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE TWO LINES HERE AS WELL ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(authData.livekitUrl, authData.livekitToken, authData.roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      
      setTimeout(async () => {
        try {
          console.log('🎤 AuthContext: Auto-enabling microphone...');
          await enableMicrophone();
          console.log('✅ AuthContext: Microphone auto-enabled successfully');
        } catch (micError) {
          console.error('⚠️ AuthContext: Microphone auto-enable failed:', micError);
          console.log('💡 Please allow microphone access to talk to the AI');
        }
      }, 2000);
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  // ... (The rest of the file: disconnectFromVoice, enableMicrophone, disableMicrophone, logout, etc. remains unchanged) ...
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const disconnectFromVoice = async () => {
    try {
      console.log('🔗 AuthContext: Disconnecting from voice...');
      await livekitService.disconnect();
      setVoiceConnected(false);
      setParticipants([]);
      console.log('✅ AuthContext: Voice disconnected successfully');
    } catch (error) {
      console.error('❌ AuthContext: Voice disconnect failed:', error);
      throw error;
    }
  };

  const enableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Enabling microphone...');
      await livekitService.enableMicrophone();
      console.log('✅ AuthContext: Microphone enabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone enable failed:', error);
      throw error;
    }
  };

  const disableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Disabling microphone...');
      await livekitService.disableMicrophone();
      console.log('✅ AuthContext: Microphone disabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone disable failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    console.log('🔐 AuthContext: Logging out...');
    if (voiceConnected) {
      try {
        await disconnectFromVoice();
      } catch (error) {
        console.warn('⚠️ AuthContext: Error disconnecting voice during logout:', error);
      }
    }
    setAuthData({
      isAuthenticated: false,
      sessionId: null,
      roomName: null,
      livekitToken: null,
      livekitUrl: null,
      userIdentity: null,
      appId: null,
      apiKey: null,
      expiresIn: 0
    });
    localStorage.removeItem('authData');
    setError(null);
    setVoiceConnected(false);
    setParticipants([]);
    console.log('🔓 Logged out');
  };

  const checkAuthStatus = async () => {
    if (!authData.apiKey) return false;
    try {
      const status = await apiService.getAuthStatus(authData.apiKey);
      if (status.is_active) {
        setAuthData(prev => ({ ...prev, expiresIn: status.expires_in }));
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('❌ Auth status check failed:', error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('authData');
    if (savedAuth) {
      try {
        const parsed = JSON.parse(savedAuth);
        setAuthData(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('❌ Failed to load saved auth data:', error);
        localStorage.removeItem('authData');
      }
    }
  }, []);

  useEffect(() => {
    if (authData.isAuthenticated) {
      const interval = setInterval(checkAuthStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.isAuthenticated]);

  // Handle browser tab/window close to ensure clean disconnect
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (authData.isAuthenticated && voiceConnected) {
        // Try to disconnect gracefully (may not complete due to browser limitations)
        try {
          await disconnectFromVoice();
        } catch (error) {
          console.warn('Could not disconnect gracefully on page unload:', error);
        }
      }
    };

    const handleVisibilityChange = () => {
      // When tab becomes hidden, consider it a potential session end
      if (document.hidden && authData.isAuthenticated) {
        // Set a timeout to disconnect if tab remains hidden
        setTimeout(() => {
          if (document.hidden && voiceConnected) {
            console.log('Tab hidden for extended time, disconnecting...');
            disconnectFromVoice();
          }
        }, 30000); // 30 seconds
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authData.isAuthenticated, voiceConnected, disconnectFromVoice]);

  const value = {
    ...authData,
    loading,
    error,
    voiceConnected,
    connectionState,
    participants,
    login,
    logout,
    checkAuthStatus,
    connectToVoice,
    connectToVoiceWithData,
    disconnectFromVoice,
    enableMicrophone,
    disableMicrophone
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};